import { toast } from 'react-toastify';
import { STRUCTURING_API } from './structuringUtils';

export type FormData = {
    operationName: string;
    files: File[];
    assignorName: string;
};

export type DocumentResult = {
    document_title: string;
    source_filename: string;
    processing_status: string;
    processing_error: string | null;
    receivables_count: number;
};

export type UploadState = {
    isProcessing: boolean;
    summary: any | null;
    processingStatus: string;
    processedFiles: any[];
    showSummary: boolean;
    operationCode: string;
    uploadProgress: any | null;
    isPolling: boolean;
    documentResults: DocumentResult[];
    totalFilesUploaded: number;
    operationId: string;
    creditOperationsId: string;
    operationStatus: string;
};

export const initialUploadState: UploadState = {
    isProcessing: false,
    summary: null,
    processingStatus: '',
    processedFiles: [],
    showSummary: false,
    operationCode: '',
    uploadProgress: null,
    isPolling: false,
    documentResults: [],
    totalFilesUploaded: 0,
    operationId: '',
    creditOperationsId: '',
    operationStatus: '',
};

export const validateForm = (formData: FormData): { valid: boolean; error?: string } => {
    if (!formData.operationName.trim()) {
        return { valid: false, error: 'Nome da operação é obrigatório' };
    }
    if (formData.files.length === 0) {
        return { valid: false, error: 'Selecione pelo menos um arquivo' };
    }
    return { valid: true };
};

export const showValidationError = (error: string) => { toast.error(error); };
export const showSuccess = (message: string) => { toast.success(message); };
export const showError = (message: string) => { toast.error(message); };

const buildHeaders = () => ({
    'x-api-key': STRUCTURING_API.key || '',
    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
});

export const findOperationByCode = async (operationCode: string): Promise<string | null> => {
    // Retry com delay para aguardar persistência do registro
    for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) await new Promise(r => setTimeout(r, 2000));
        try {
            const response = await fetch(
                `${STRUCTURING_API.url}/operations?page=0&pageSize=20`,
                { headers: buildHeaders() }
            );
            if (!response.ok) continue;
            const data = await response.json();
            const items = data?.content || data?.items || data || [];
            const list = Array.isArray(items) ? items : [];
            const op = list.find((o: any) => o.operationCode === operationCode);
            if (op?.id) return op.id.toString();
        } catch {
            continue;
        }
    }
    return null;
};

export const fetchOperationPreview = async (creditOperationsId: string): Promise<{
    status: string;
    document_results: DocumentResult[];
} | null> => {
    try {
        const response = await fetch(
            `${STRUCTURING_API.url}/operation/preview/${creditOperationsId}`,
            { headers: buildHeaders() }
        );
        if (!response.ok) return null;
        const data = await response.json();
        const status = data.status || 'processing';
        const results: DocumentResult[] = (data.documents || []).map((doc: any) => ({
            document_title: doc.document_title || '',
            source_filename: doc.source_filename || '',
            processing_status: doc.processing_status || 'pending',
            processing_error: doc.processing_error || null,
            receivables_count: doc.receivables_count || 0,
        }));
        return { status, document_results: results };
    } catch {
        return null;
    }
};

export const triggerProcessing = async (operationId: string): Promise<boolean> => {
    try {
        const response = await fetch(
            `${STRUCTURING_API.url}/operations/${operationId}/process`,
            { method: 'POST', headers: buildHeaders() }
        );
        return response.ok;
    } catch {
        return false;
    }
};
